import React, { useState, useEffect, Component, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

// Components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

// Auth Pages
import Login from './components/auth/Login';
import AdminLogin from './components/auth/AdminLogin';
import Register from './components/auth/Register';
import ResetPassword from './components/auth/ResetPassword';
import ForgotPassword from './components/auth/ForgotPassword';
import AdminInit from './components/admin/AdminInit';

// Context
import AuthContext from './context/AuthContext';
import { LicenseProvider } from './context/LicenseContext';
import PrivateRoute from './components/routing/PrivateRoute';
import AdminRoute from './components/routing/AdminRoute';

// License Components
import ReadOnlyBanner from './components/license/ReadOnlyBanner';
import LicenseWarning from './components/license/LicenseWarning';

// Customer Pages — lazy loaded for code splitting
const Dashboard = lazy(() => import('./components/dashboard/Dashboard'));
const Projects = lazy(() => import('./components/projects/Projects'));
const ProjectDetail = lazy(() => import('./components/projects/ProjectDetail'));
const CreateProject = lazy(() => import('./components/projects/CreateProject'));
const Clients = lazy(() => import('./components/clients/Clients'));
const ClientDetail = lazy(() => import('./components/clients/ClientDetail'));
const CreateClient = lazy(() => import('./components/clients/CreateClient'));
const Documents = lazy(() => import('./components/documents/Documents'));
const DocumentsShowcase = lazy(() => import('./components/documents/DocumentsShowcase'));
const Employees = lazy(() => import('./components/employees/Employees'));
const EmployeeDetail = lazy(() => import('./components/employees/EmployeeDetail'));
const CreateEmployee = lazy(() => import('./components/employees/CreateEmployee'));
const EditEmployee = lazy(() => import('./components/employees/EditEmployee'));
const TimeEntries = lazy(() => import('./components/time-entries/TimeEntries'));
const AssignEmployees = lazy(() => import('./components/projects/AssignEmployees'));
const PaymentSchedule = lazy(() => import('./components/projects/payment-schedule/PaymentSchedule'));
const ReportsPage = lazy(() => import('./components/reports/ReportsPage'));
const Payments = lazy(() => import('./components/payments/Payments'));
const CreatePayment = lazy(() => import('./components/payments/CreatePayment'));
const EditPayment = lazy(() => import('./components/payments/EditPayment'));
const ProjectPaymentDetails = lazy(() => import('./components/payments/ProjectPaymentDetails'));
const LicenseManagement = lazy(() => import('./components/license/LicenseManagement'));
const SigningPage = lazy(() => import('./components/signing/SigningPage'));
const SignupPage = lazy(() => import('./components/public/SignupPage'));
const SignupSuccess = lazy(() => import('./components/public/SignupSuccess'));
const MileageTracker = lazy(() => import('./components/mileage/MileageTracker'));
const MileageReport = lazy(() => import('./components/mileage/MileageReport'));
const SubcontractorList = lazy(() => import('./components/subcontractors/SubcontractorList'));
const SubcontractorDetail = lazy(() => import('./components/subcontractors/SubcontractorDetail'));
const Report1099 = lazy(() => import('./components/subcontractors/Report1099'));
const JobProfitability = lazy(() => import('./components/reports/JobProfitability'));
const QuickBooksSettings = lazy(() => import('./components/quickbooks/QuickBooksSettings'));
const SquareSettings = lazy(() => import('./components/square/SquareSettings'));

// Admin Pages — lazy loaded
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const CustomersList = lazy(() => import('./components/admin/CustomersList'));
const CreateCustomer = lazy(() => import('./components/admin/CreateCustomer'));
const CustomerDetail = lazy(() => import('./components/admin/CustomerDetail'));
const UsersList = lazy(() => import('./components/admin/UsersList'));
const CreateUser = lazy(() => import('./components/admin/CreateUser'));
const PendingSignups = lazy(() => import('./components/admin/PendingSignups'));

// Error Boundary to catch unhandled React errors
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Send error to backend log file
    try {
      fetch('/api/log/error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: error.message || 'Unknown React error',
          stack: error.stack,
          componentStack: errorInfo?.componentStack,
          url: window.location.href
        })
      }).catch(() => {});
    } catch (e) {
      // Don't let logging errors break the error boundary
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="container text-center mt-5">
          <h2>Something went wrong</h2>
          <p className="text-muted">An unexpected error occurred. Please try refreshing the page.</p>
          <button
            className="btn btn-primary"
            onClick={() => {
              this.setState({ hasError: false, error: null });
              window.location.href = '/';
            }}
          >
            Go to Home
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Global error handler — catches JS errors outside React components
  useEffect(() => {
    const logToServer = (payload) => {
      try {
        fetch('/api/log/error', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }).catch(() => {});
      } catch (e) { /* don't let logging break the app */ }
    };

    const handleWindowError = (event) => {
      logToServer({
        message: event.message || 'Uncaught error',
        stack: event.error?.stack,
        url: window.location.href
      });
    };

    const handleUnhandledRejection = (event) => {
      logToServer({
        message: event.reason?.message || 'Unhandled promise rejection',
        stack: event.reason?.stack,
        url: window.location.href
      });
    };

    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => {
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Check if user is authenticated
  useEffect(() => {
    const controller = new AbortController();

    const checkAuth = async () => {
      if (token) {
        try {
          const response = await fetch('/api/auth/user', {
            headers: { 'x-auth-token': token },
            signal: controller.signal
          });

          const data = await response.json();

          if (response.ok) {
            setUser(data);
            setIsAuthenticated(true);
          } else {
            setToken(null);
            localStorage.removeItem('token');
            setIsAuthenticated(false);
            setUser(null);
          }
        } catch (err) {
          if (err.name === 'AbortError') return;
          console.error('Error checking authentication', err);
          setToken(null);
          localStorage.removeItem('token');
          setIsAuthenticated(false);
          setUser(null);
        }
      }

      setIsLoading(false);
    };

    checkAuth();

    return () => controller.abort();
  }, [token]);
  
  const login = (newToken, newUser) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    if (newUser) {
      setUser(newUser);
    }
    setIsAuthenticated(true);
  };
  
  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  };
  
  if (isLoading) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, token, login, logout }}>
      <LicenseProvider>
        <ErrorBoundary>
        <Router>
          <div className="App d-flex flex-column min-vh-100">
            <Header />
            <main className="flex-grow-1 my-3">
              <div className="container">
                {isAuthenticated && (
                  <>
                    <ReadOnlyBanner />
                    <LicenseWarning />
                  </>
                )}
                <Suspense fallback={<div className="text-center mt-5">Loading...</div>}>
                <Routes>
                <Route path="/admin/init" element={<AdminInit />} />
                <Route path="/" element={isAuthenticated ? (
                  user?.isAdmin ? <Navigate to="/admin/dashboard" /> : <Navigate to="/dashboard" />
                ) : <Login />} />
                <Route path="/login" element={<Login />} />
                <Route path="/admin" element={<AdminLogin />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:customer/:token" element={<ResetPassword />} />
                <Route path="/sign/:token" element={<SigningPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/signup/success" element={<SignupSuccess />} />
                
                <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                <Route path="/projects" element={<PrivateRoute><Projects /></PrivateRoute>} />
                <Route path="/projects/:id" element={<PrivateRoute><ProjectDetail /></PrivateRoute>} />
                <Route path="/projects/create" element={<PrivateRoute><CreateProject /></PrivateRoute>} />
                <Route path="/clients" element={<PrivateRoute><Clients /></PrivateRoute>} />
                <Route path="/clients/:id" element={<PrivateRoute><ClientDetail /></PrivateRoute>} />
                <Route path="/clients/create" element={<PrivateRoute><CreateClient /></PrivateRoute>} />
                <Route path="/documents" element={<PrivateRoute><Documents /></PrivateRoute>} />
                <Route path="/projects/:id/documents-preview" element={<PrivateRoute><DocumentsShowcase /></PrivateRoute>} />
                <Route path="/projects/:id/document-preview" element={<PrivateRoute><DocumentsShowcase /></PrivateRoute>} />
                <Route path="/employees" element={<PrivateRoute><Employees /></PrivateRoute>} />
                <Route path="/employees/:id" element={<PrivateRoute><EmployeeDetail /></PrivateRoute>} />
                <Route path="/employees/:id/edit" element={<PrivateRoute><EditEmployee /></PrivateRoute>} />
                <Route path="/employees/create" element={<PrivateRoute><CreateEmployee /></PrivateRoute>} />
                <Route path="/projects/:projectId/time-entries" element={<PrivateRoute><TimeEntries /></PrivateRoute>} />
                <Route path="/projects/:projectId/assign-employees" element={<PrivateRoute><AssignEmployees /></PrivateRoute>} />
                <Route path="/projects/:projectId/payment-schedule" element={<PrivateRoute><PaymentSchedule /></PrivateRoute>} />
                <Route path="/reports" element={<PrivateRoute><ReportsPage /></PrivateRoute>} />
                <Route path="/payments" element={<PrivateRoute><Payments /></PrivateRoute>} />
                <Route path="/payments/:projectId" element={<PrivateRoute><ProjectPaymentDetails /></PrivateRoute>} />
                <Route path="/payments/create" element={<PrivateRoute><CreatePayment /></PrivateRoute>} />
                <Route path="/payments/edit/:id" element={<PrivateRoute><EditPayment /></PrivateRoute>} />
                <Route path="/mileage" element={<PrivateRoute><MileageTracker /></PrivateRoute>} />
                <Route path="/mileage/report" element={<PrivateRoute><MileageReport /></PrivateRoute>} />
                <Route path="/subcontractors" element={<PrivateRoute><SubcontractorList /></PrivateRoute>} />
                <Route path="/subcontractors/:id" element={<PrivateRoute><SubcontractorDetail /></PrivateRoute>} />
                <Route path="/reports/1099" element={<PrivateRoute><Report1099 /></PrivateRoute>} />
                <Route path="/reports/profitability" element={<PrivateRoute><JobProfitability /></PrivateRoute>} />
                <Route path="/reports/profitability/:projectId" element={<PrivateRoute><JobProfitability /></PrivateRoute>} />
                <Route path="/quickbooks" element={<PrivateRoute><QuickBooksSettings /></PrivateRoute>} />
                <Route path="/square" element={<PrivateRoute><SquareSettings /></PrivateRoute>} />
                <Route path="/license" element={<PrivateRoute><LicenseManagement /></PrivateRoute>} />
                
                <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
                <Route path="/admin/customers" element={<AdminRoute><CustomersList /></AdminRoute>} />
                <Route path="/admin/customers/create" element={<AdminRoute><CreateCustomer /></AdminRoute>} />
                <Route path="/admin/customers/:id" element={<AdminRoute><CustomerDetail /></AdminRoute>} />
                <Route path="/admin/users" element={<AdminRoute><UsersList /></AdminRoute>} />
                <Route path="/admin/users/create" element={<AdminRoute><CreateUser /></AdminRoute>} />
                <Route path="/admin/pending-signups" element={<AdminRoute><PendingSignups /></AdminRoute>} />
                </Routes>
                </Suspense>
              </div>
            </main>
            <Footer />
          </div>
        </Router>
        </ErrorBoundary>
      </LicenseProvider>
    </AuthContext.Provider>
  );
}

export default App;
